import { useArticleDownloadUsage } from '@/api/articleDownloadUsage';
import { Button } from '@/components/Elements';
import { InputField, SelectField } from '@/components/Form';
import { useAnalyzeTotalResultCountAtomValue } from '@/features/analyze/atoms/analyzeTotalResultCountAtom';
import { useDownloadArticles } from '@/features/analyze/hooks/useDownloadArticle';
import { type SupportedLanguage } from '@/lib/i18n/config';
import { getCurrentLanguage } from '@/lib/i18n/getCurrentLanguage';
import {
  findLanguageOption,
  getLanguageOptions,
} from '@/lib/i18n/languageLabels';
import { XMarkIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const MAX_DOWNLOAD_LIMIT = 1000;

const calcMaxDownloadLimitThisTime = (
  remainingSearchResults: number,
  monthlyDownloadRemaining?: number,
  unlimitedDownload?: boolean
) => {
  if (unlimitedDownload) {
    return Math.min(MAX_DOWNLOAD_LIMIT, remainingSearchResults);
  }

  return Math.min(
    MAX_DOWNLOAD_LIMIT,
    remainingSearchResults,
    monthlyDownloadRemaining || 0
  );
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export const DialogContent = ({ isOpen, onClose }: Props) => {
  const { t } = useTranslation();
  const { mutation, currentOffset, resetOffset } = useDownloadArticles();
  const searchResultCount = useAnalyzeTotalResultCountAtomValue();
  const [isError, setIsError] = useState(false);
  const [isLimitError, setIsLimitError] = useState(false);
  const [inputtedDownloadCount, setInputtedDownloadCount] = useState(0);
  const [downloadLanguage, setDownloadLanguage] =
    useState<SupportedLanguage>(getCurrentLanguage());
  useEffect(() => {
    resetOffset();
  }, [downloadLanguage, resetOffset]);
  const { data: usageData } = useArticleDownloadUsage({
    // モーダルが開いたらリクエストするようにする
    config: { enabled: isOpen },
  });
  const unlimitedDownload = usageData?.unlimited === true;
  // 未ダウンロードの検索結果件数
  const remainingSearchResults = searchResultCount - currentOffset;

  const maxDownloadLimitThisTime = calcMaxDownloadLimitThisTime(
    remainingSearchResults,
    usageData?.remaining,
    unlimitedDownload
  );

  const cannotDownload = maxDownloadLimitThisTime <= 0;

  const handleDownload = () => {
    if (cannotDownload) return;
    if (inputtedDownloadCount <= 0) return;

    const downloadCount = Math.min(
      inputtedDownloadCount,
      maxDownloadLimitThisTime
    );

    mutation.mutate(
      { downloadCount, language: downloadLanguage },
      {
        onSuccess: () => {
          // Do nothing on success - we'll handle the UI state through the button
        },
        onError: (error: unknown) => {
          setIsError(true);
          setIsLimitError(
            (error as { response?: { status?: number } })?.response?.status ===
              429
          );
        },
      }
    );
  };

  const languageOptions = getLanguageOptions(t);
  const selectedLanguage = findLanguageOption(
    downloadLanguage,
    languageOptions
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (cannotDownload) return;

    const value = parseInt(e.target.value, 10);
    if (isNaN(value)) return;

    let limitedValue = Math.max(value, 1);
    limitedValue = Math.min(limitedValue, maxDownloadLimitThisTime);

    setInputtedDownloadCount(limitedValue);
  };

  return (
    <div className="relative">
      {/* Updated close button with consistent positioning */}
      <button
        onClick={onClose}
        className="absolute -right-2 -top-14 z-10 flex size-8 items-center justify-center text-gray-400 hover:text-gray-600"
        aria-label="Close dialog"
      >
        <XMarkIcon className="size-5" />
      </button>

      <div className="space-y-6 pt-8">
        <div className="grid grid-cols-2 gap-4">
          {/* 月間ダウンロード情報 */}
          <div className="rounded-lg bg-primary-light-teal p-4">
            <div className="text-sm text-gray-600">{t('月間ダウンロード')}</div>
            <div className="mt-1 flex items-baseline">
              <span className="text-xl font-medium text-primary-teal">
                {usageData?.remainingDisplayValue}
              </span>
              {!unlimitedDownload && (
                <span className="ml-1 text-sm text-gray-600">
                  / {usageData?.limitDisplayValue}
                </span>
              )}
            </div>
          </div>

          {/* 制限情報 */}
          <div className="rounded-lg bg-gray-100/80 p-4">
            <h4 className="text-sm text-gray-700">{t('ダウンロード制限')}</h4>
            <div className="mt-1 space-y-1">
              <div className="flex items-end justify-between text-sm text-gray-600">
                <span className="text-xs">{t('1回の上限')}</span>
                <span>{MAX_DOWNLOAD_LIMIT.toLocaleString()}</span>
              </div>
              <div className="flex items-end justify-between text-sm text-gray-600">
                <span className="text-xs">{t('検索結果')}</span>
                <span>{searchResultCount.toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>

        {/* ダウンロード件数選択 */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-sm text-gray-700">
              {t('ダウンロード件数を選択')}
            </h4>
            <div className="flex items-center space-x-2">
              <InputField
                type="number"
                value={inputtedDownloadCount}
                onChange={handleInputChange}
                min="1"
                max={maxDownloadLimitThisTime}
                disabled={cannotDownload}
              />
              <span className="text-sm text-gray-600">{t('件')}</span>
            </div>
          </div>
          <input
            type="range"
            value={inputtedDownloadCount}
            onChange={handleInputChange}
            min="1"
            max={maxDownloadLimitThisTime}
            className="w-full"
            disabled={cannotDownload}
          />
        </div>

        {/* ダウンロード言語選択 */}
        <div className="space-y-2">
          <SelectField
            label={t('ダウンロード言語')}
            className="text-black"
            options={languageOptions}
            value={selectedLanguage}
            isSearchable={false}
            onChange={(option) => {
              if (!option) return;
              setDownloadLanguage(option.value);
            }}
          />
        </div>

        {/* 注意事項 */}
        <div>
          <ul className="list-disc space-y-2 pl-5 text-sm text-gray-600 marker:text-gray-400">
            <li>
              {t('ダウンロード制限は日本時間の毎月1日にリセットされます')}
            </li>
            <li>{t('検索条件に一致する記事がダウンロードされます')}</li>
            <li>{t('最新の記事から選択した件数ダウンロードされます')}</li>
            <li>
              {t(
                'データ更新中は、検索結果と異なる記事がダウンロードされる場合があります'
              )}
            </li>
            {currentOffset > 0 && remainingSearchResults > 0 && (
              <li className="text-red-600">
                {t('この画面を閉じると続きからダウンロードできなくなります')}
              </li>
            )}
          </ul>
        </div>
      </div>

      {isError && (
        <div className="mt-4 rounded-lg bg-red-100 p-4 text-xs text-red-600">
          {isLimitError
            ? t('ダウンロード上限に達しました')
            : t('ダウンロードに失敗しました。時間をおいて再度お試しください。')}
        </div>
      )}

      <div className="mt-6 space-y-4">
        {currentOffset > 0 && (
          <div className="flex items-center">
            <span className="text-sm text-gray-600">
              {t('ダウンロード済み')}: {currentOffset.toLocaleString()}{' '}
              {t('件')} / {searchResultCount.toLocaleString()} {t('件')}
            </span>
          </div>
        )}
        <div className="flex justify-end space-x-3">
          <Button size="sm" color="inverse" onClick={onClose} noBordered>
            {t('キャンセル')}
          </Button>
          {currentOffset === 0 ? (
            // 初回ダウンロード用ボタン
            <Button
              size="sm"
              onClick={handleDownload}
              isLoading={mutation.isPending}
              disabled={cannotDownload || mutation.isPending}
            >
              {t('download_item', { count: inputtedDownloadCount })}
            </Button>
          ) : (
            // 追加ダウンロード用ボタン
            <Button
              size="sm"
              color="primary"
              onClick={handleDownload}
              isLoading={mutation.isPending}
              disabled={
                currentOffset >= searchResultCount ||
                cannotDownload ||
                mutation.isPending
              }
            >
              {currentOffset >= searchResultCount
                ? `${t('全件')} ${t('ダウンロード済み')}`
                : `${t('次の')} ${inputtedDownloadCount.toLocaleString()} ${t(
                    '件をダウンロード'
                  )}`}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
